/**
 * Seeded randomness (GDD §5.3, §11).
 * World generation reuses the concept's mulberry32 stream so scenery matches the reference frames;
 * gameplay sections get their own seeds so every run differs but any bug can be replayed.
 */

export type Rng = () => number;

/** mulberry32, identical to the concept renderer's `mulberry` (concept L16). */
export function mulberry32(seed: number): Rng {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A mulberry32 stream that can be reseeded in place (a new section, a new run) without creating anything. */
export interface SeededRng extends Rng {
  reseed(seed: number): void;
}

/**
 * The same generator as `mulberry32`, with its state kept in an object field: V8 stores a double field in place,
 * where a closure variable holding a number above 2^30 is boxed on every write (GDD §10.5).
 */
export function seededRng(seed: number): SeededRng {
  const st = { s: seed >>> 0 };
  const next = (() => {
    st.s = (st.s + 0x6d2b79f5) >>> 0;
    let t = st.s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }) as SeededRng;
  next.reseed = (s: number) => {
    st.s = s >>> 0;
  };
  return next;
}

/** murmur3 finaliser: a bijection on uint32 with full avalanche. */
function fmix32(h: number): number {
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return (h ^ (h >>> 16)) >>> 0;
}

/**
 * Seed for one 800 m section of a run. For a fixed run the mapping is injective
 * (odd-multiplier step through a bijection), so no two sections of a run share a seed.
 */
export function sectionSeed(runSeed: number, sectionIndex: number): number {
  return fmix32((fmix32(runSeed >>> 0) + Math.imul(sectionIndex, 0x9e3779b9)) >>> 0);
}

/** Seed of a run's combat stream (aim spread, cooldown jitter, the autopilot's dodge rolls): distinct from sections. */
export function combatSeed(runSeed: number): number {
  return fmix32((fmix32(runSeed >>> 0) ^ 0xa5a5a5a5) >>> 0);
}

export function newRunSeed(): number {
  const out = new Uint32Array(1);
  crypto.getRandomValues(out);
  return out[0] ?? 0;
}
